import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core'

import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { TranslateModule } from '@ngx-translate/core'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

@UntilDestroy()
@Component({
  selector: 'gb-presenter-tools-video',
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, TranslateModule],
  templateUrl: './gb-presenter-tools-video.component.html',
  styleUrl: './gb-presenter-tools-video.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterToolsVideoComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) toolsFormGroup!: FormGroup

  private readonly formChanged$ = new Subject<void>()

  private hasSubscribed = false

  ngOnDestroy(): void {
    this.formChanged$.next()
    this.formChanged$.complete()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['toolsFormGroup']?.currentValue) return

    // if the form-group instance changes, re-subscribe.
    if (this.hasSubscribed) {
      this.formChanged$.next()
    }

    this.hasSubscribed = true

    this.embedUrlControl.valueChanges.pipe(untilDestroyed(this), takeUntil(this.formChanged$)).subscribe((raw) => {
      const normalized = normalizeVideoEmbedUrl(raw)

      if (raw !== normalized) {
        this.embedUrlControl.setValue(normalized, { emitEvent: false })
      }

      this.toolsFormGroup.markAsDirty()
    })
  }

  get embedUrlControl(): FormControl {
    return this.toolsFormGroup.get('video_url') as FormControl
  }
}

const YOUTUBE_EMBED_HOSTS = new Set([
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'youtube-nocookie.com',
])
const VIMEO_EMBED_HOSTS = new Set(['player.vimeo.com'])

function normalizeVideoEmbedUrl(raw: unknown): string {
  const input = typeof raw === 'string' ? raw.trim() : ''
  if (!input) return ''

  // If they pasted an iframe, extract src
  const iframeSrcMatch = input.match(/<iframe[^>]+src=["']([^"']+)["'][^>]*>/i)
  const candidate = iframeSrcMatch?.[1] ?? input

  let url: URL
  try {
    url = new URL(candidate)
  } catch {
    return input
  }

  if (url.protocol !== 'https:') {
    return input
  }

  // YouTube watch/share -> embed
  if (url.hostname.endsWith('youtube.com') || url.hostname.endsWith('youtube-nocookie.com')) {
    const videoId = url.pathname.startsWith('/watch')
      ? url.searchParams.get('v')
      : url.pathname.startsWith('/shorts/')
        ? url.pathname.split('/')[2]
        : null

    if (videoId) {
      const embed = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`)
      const start = url.searchParams.get('t') || url.searchParams.get('start')
      if (start) {
        const seconds = parseStartSeconds(start)
        if (seconds != null) {
          embed.searchParams.set('start', String(seconds))
        }
      }
      return embed.toString()
    }

    // Already embed URL?
    if (url.pathname.startsWith('/embed/')) {
      return isAllowedEmbedHost(url) ? url.toString() : input
    }

    return input
  }

  // youtu.be -> embed
  if (url.hostname === 'youtu.be') {
    const videoId = url.pathname.replace('/', '')
    if (!videoId) return input

    const embed = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`)
    const start = url.searchParams.get('t') || url.searchParams.get('start')
    if (start) {
      const seconds = parseStartSeconds(start)
      if (seconds != null) {
        embed.searchParams.set('start', String(seconds))
      }
    }
    return embed.toString()
  }

  // Vimeo -> embed
  if (url.hostname.endsWith('vimeo.com')) {
    // If already player.vimeo.com/video/:id
    if (url.hostname === 'player.vimeo.com' && url.pathname.startsWith('/video/')) {
      return isAllowedEmbedHost(url) ? url.toString() : input
    }

    // vimeo.com/:id
    const idMatch = url.pathname.match(/^\/(\d+)(?:$|\/)/)
    const videoId = idMatch?.[1]
    if (!videoId) return input

    const embed = new URL(`https://player.vimeo.com/video/${videoId}`)
    return embed.toString()
  }

  // Not allowed host
  return input
}

function isAllowedEmbedHost(url: URL): boolean {
  return YOUTUBE_EMBED_HOSTS.has(url.hostname) || VIMEO_EMBED_HOSTS.has(url.hostname)
}

function parseStartSeconds(value: string): number | null {
  // supports 90, 90s, 1m30s
  if (/^\d+$/.test(value)) return Number(value)
  const secondsOnly = value.match(/^(\d+)s$/)
  if (secondsOnly) return Number(secondsOnly[1])

  const m = value.match(/^(?:(\d+)m)?(?:(\d+)s)?$/)
  if (!m) return null

  const minutes = m[1] ? Number(m[1]) : 0
  const seconds = m[2] ? Number(m[2]) : 0

  const total = minutes * 60 + seconds
  return Number.isFinite(total) ? total : null
}
